import React, { useEffect, useState } from "react";
import axios from "axios";
import { IoClose } from "react-icons/io5";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../../Config";
import "../Customer/customer.css";
import "../PurchaseIndents/purchaseIndents.css";
import "./barrelInward.css";

const AddInward = ({ onClose, onSuccess }) => {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([
    { product_id: "", quantity: "", uom: "KL", barrel_count: "" },
  ]);

  const [form, setForm] = useState({
    receipt_date: "",
    supplier_name: "",
    vehicle_number: "",
    driver_name: "",
    invoice_number: "",
    remarks: "",
  });

  const [file, setFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    msg: "",
    type: "success",
  });

  const showSnackbar = (msg, type = "success") =>
    setSnackbar({ open: true, msg, type });

  /* ---------------- FETCH PRODUCTS ---------------- */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("authKey");
        const res = await axios.get(`${API_URL}/api/barrel/products`, {
          headers: { Authorization: token },
        });
        setProducts(res.data.products || []);
      } catch (e) {
        showSnackbar("Failed to load products", "error");
      }
    };
    fetchProducts();
  }, []);

  /* ---------------- HANDLERS ---------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    if (field === "product_id") {
      const alreadyExists = items.some(
        (item, i) => item.product_id === value && i !== index,
      );

      if (alreadyExists) {
        setSnackbar({
          open: true,
          msg: "This product is already added",
          type: "error",
        });
        return;
      }
    }

    const updated = [...items];
    updated[index][field] = value;

    if (field === "quantity") {
      const qty = parseFloat(value || 0);
      updated[index].barrel_count = Math.ceil((qty * 1000) / 200);
    }

    setItems(updated);
  };

  const addItemRow = () => {
    setItems((prev) => [
      ...prev,
      { product_id: "", quantity: "", uom: "KL", barrel_count: "" },
    ]);
  };

  const removeItemRow = (index) => {
    if (items.length === 1) {
      showSnackbar("At least one product row is required", "error");
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    if (!form.receipt_date)
      return showSnackbar("Receipt Date is required", "error");
    if (!form.supplier_name.trim())
      return showSnackbar("Supplier Name is required", "error");
    if (!form.vehicle_number.trim())
      return showSnackbar("Vehicle Number is required", "error");
    // if (!form.driver_name.trim())
    //   return showSnackbar("Driver Name is required", "error");
    if (!file) return showSnackbar("Invoice file is required", "error");
    // Items validation
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      if (!row.product_id)
        return showSnackbar(`Select product in row ${i + 1}`, "error");
      if (!row.quantity || Number(row.quantity) <= 0)
        return showSnackbar(`Enter quantity in row ${i + 1}`, "error");
    }

    const payload = {
      receipt_date: form.receipt_date,
      supplier_name: form.supplier_name,
      vehicle_number: form.vehicle_number,
      driver_name: form.driver_name,
      invoice_number: form.invoice_number,
      remarks: form.remarks,
      items: items.map((i) => ({
        product_id: i.product_id,
        quantity: Number(i.quantity),
        uom: i.uom || "KL",
        barrel_count: i.barrel_count ? Number(i.barrel_count) : undefined,
      })),
    };

    const formData = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (k === "items") formData.append("items", JSON.stringify(v));
      else formData.append(k, v ?? "");
    });
    if (file) formData.append("invoice_file", file);

    try {
      setIsSaving(true);
      const token = localStorage.getItem("authKey");
      await axios.post(`${API_URL}/api/barrel/receipts`, formData, {
        headers: { Authorization: token },
      });

      showSnackbar("Stock receipt added successfully", "success");
      setTimeout(() => onSuccess(), 600);
    } catch (err) {
      showSnackbar(
        err?.response?.data?.message || "Failed to save receipt",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className="barrels_popup-overlay" style={{ padding: "0px" }}>
        <div className="barrels_popup-modal">
          <div className="barrels_popup-header">
            <h4>Add Stock</h4>
            <span className="barrels_close-btn" onClick={onClose}>
              <IoClose />
            </span>
          </div>

          <div className="barrels_popup-overlay-inward-body">
            <div className="barrels_popup-body">
              {/* BASIC INFO */}
              <div className="barrels_form-group">
                <label>
                  Receipt Date <span className="required_label">*</span>
                </label>
                <input
                  type="date"
                  name="receipt_date"
                  value={form.receipt_date}
                  onChange={handleInputChange}
                  style={{ textTransform: "uppercase" }}
                />
              </div>

              <div className="barrels_form-group">
                <label>
                  Supplier Name <span className="required_label">*</span>
                </label>
                <input
                  type="text"
                  name="supplier_name"
                  value={form.supplier_name}
                  onChange={handleInputChange}
                  placeholder="Enter supplier name"
                />
              </div>

              <div className="barrels_form-group">
                <label>
                  Vehicle Number <span className="required_label">*</span>
                </label>
                <input
                  type="text"
                  name="vehicle_number"
                  value={form.vehicle_number}
                  onChange={handleInputChange}
                  placeholder="Enter vehicle number"
                />
              </div>

              <div className="barrels_form-group">
                <label>Driver Name</label>
                <input
                  type="text"
                  name="driver_name"
                  value={form.driver_name}
                  onChange={handleInputChange}
                  placeholder="Enter driver name"
                />
              </div>

              {/* INVOICE NUMBER */}
              <div className="barrels_form-group">
                <label>Invoice Number</label>
                <input
                  type="text"
                  name="invoice_number"
                  value={form.invoice_number}
                  onChange={handleInputChange}
                  placeholder="Enter invoice number"
                />
              </div>

              <div className="barrels_form-group">
                <label>
                  Products <span className="required_label">*</span>
                </label>

                <div className="">
                  <div className="barrels_product_item_div">
                    {items.map((item, index) => (
                      <div key={index} className="barrels_product_row">
                        <div className="barrels_product_product_row">
                          <div className="barrels_product_select_wrap">
                            <select
                              value={item.product_id}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "product_id",
                                  e.target.value,
                                )
                              }
                              className="barrels_product_select"
                            >
                              <option value="">Select Product</option>
                              {products.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.product_name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <button
                            type="button"
                            className="barrel-inward-remove-btn"
                            onClick={() => removeItemRow(index)}
                            title="Remove"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="barrels_product_qty_row">
                          <input
                            type="number"
                            step="0.01"
                            placeholder="Quantity (KL)"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            className="barrels_product_qty_input"
                          />

                          <input
                            type="text"
                            readOnly
                            value={item.barrel_count}
                            placeholder="Barrels (Auto Calculated)"
                            className="barrels_product_qty_input"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="add-row-btn add-row-btn-inward"
                    onClick={addItemRow}
                  >
                    + Add Product
                  </button>
                </div>
              </div>

              {/* REMARKS */}
              <div className="barrels_form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  rows="3"
                  value={form.remarks}
                  onChange={handleInputChange}
                  placeholder="Enter remarks"
                />
              </div>

              {/* FILE */}
              <div className="barrels_form-group">
                <label>
                  Upload Invoice <span className="required_label">*</span>
                </label>
                <input
                  type="file"
                  required
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </div>
            </div>

            <div className="barrels_popup-footer">
              <button
                className="barrels_btn barrels_btn-save"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </button>
              <button
                className="barrels_btn barrels_btn-cancel"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          style={{ marginTop: "35px", marginLeft: "20px" }}
        >
          <MuiAlert severity={snackbar.type} variant="filled" elevation={6}>
            {snackbar.msg}
          </MuiAlert>
        </Snackbar>
      </div>
    </>
  );
};

export default AddInward;
